import { motion } from "framer-motion";

/**
 * HeroArt — an award-worthy abstract SVG composition.
 * White canvas, navy primary, muted gold accents.
 * Represents the evolution of ideas → execution through:
 *  - a blueprint grid (planning)
 *  - concentric rings (logo emblem echo, precision)
 *  - a flowing dual-ribbon (from the logo wave — ideas in motion)
 *  - an isometric architectural block (execution)
 *  - drifting particles (network, delivery)
 *  - small blueprint annotations (engineering precision)
 * All motion is slow, elegant, and additive.
 */
export default function HeroArt() {
  return (
    <div className="relative w-full h-full" aria-hidden="true">
      {/* Very soft radial wash for depth */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(60% 60% at 65% 45%, rgba(197,160,89,0.10) 0%, rgba(197,160,89,0) 60%), radial-gradient(80% 80% at 40% 80%, rgba(10,25,47,0.05) 0%, rgba(10,25,47,0) 60%)",
        }}
      />

      <motion.svg
        viewBox="0 0 800 800"
        className="absolute inset-0 w-full h-full"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.4, ease: [0.7, 0, 0.3, 1], delay: 0.3 }}
      >
        <defs>
          <pattern id="hero-grid" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(10,25,47,0.09)" strokeWidth="0.6" />
          </pattern>
          <pattern id="hero-grid-fine" x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse">
            <path d="M 8 0 L 0 0 0 8" fill="none" stroke="rgba(10,25,47,0.05)" strokeWidth="0.4" />
          </pattern>
          <radialGradient id="glow-navy" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(10,25,47,0.10)" />
            <stop offset="100%" stopColor="rgba(10,25,47,0)" />
          </radialGradient>
          <linearGradient id="ribbon-navy" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#0A192F" stopOpacity="0" />
            <stop offset="30%" stopColor="#0A192F" stopOpacity="1" />
            <stop offset="100%" stopColor="#0A192F" stopOpacity="0.35" />
          </linearGradient>
          <linearGradient id="ribbon-gold" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#C5A059" stopOpacity="0" />
            <stop offset="50%" stopColor="#C5A059" stopOpacity="1" />
            <stop offset="100%" stopColor="#C5A059" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Blueprint grid fill */}
        <rect x="0" y="0" width="800" height="800" fill="url(#hero-grid-fine)" />
        <rect x="0" y="0" width="800" height="800" fill="url(#hero-grid)" />

        {/* Soft ambient glow behind main emblem */}
        <circle cx="470" cy="380" r="300" fill="url(#glow-navy)" />

        {/* Concentric rings — echo the logo emblem, rotate slowly */}
        <motion.g
          style={{ transformOrigin: "470px 380px" }}
          animate={{ rotate: 360 }}
          transition={{ duration: 90, ease: "linear", repeat: Infinity }}
        >
          <circle cx="470" cy="380" r="260" fill="none" stroke="#0A192F" strokeOpacity="0.32" strokeWidth="0.9" pathLength="100" strokeDasharray="82 100" strokeDashoffset="-4" strokeLinecap="round" />
          <circle cx="470" cy="380" r="260" fill="none" stroke="#C5A059" strokeOpacity="0.7" strokeWidth="1.1" pathLength="100" strokeDasharray="14 100" strokeDashoffset="-88" strokeLinecap="round" />
        </motion.g>

        <motion.g
          style={{ transformOrigin: "470px 380px" }}
          animate={{ rotate: -360 }}
          transition={{ duration: 140, ease: "linear", repeat: Infinity }}
        >
          <circle cx="470" cy="380" r="200" fill="none" stroke="#0A192F" strokeOpacity="0.24" strokeWidth="0.9" pathLength="100" strokeDasharray="1 4" />
        </motion.g>

        <circle cx="470" cy="380" r="150" fill="none" stroke="#0A192F" strokeOpacity="0.09" strokeWidth="1" />

        {/* Reveal-drawn compass lines */}
        <motion.g
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 2.2, delay: 0.7, ease: [0.7, 0, 0.3, 1] }}
        >
          <motion.line x1="470" y1="100" x2="470" y2="660" stroke="#0A192F" strokeOpacity="0.18" strokeWidth="0.8" strokeDasharray="2 4"
            initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.6, delay: 0.9 }} />
          <motion.line x1="180" y1="380" x2="760" y2="380" stroke="#0A192F" strokeOpacity="0.14" strokeWidth="0.8" strokeDasharray="2 4"
            initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.6, delay: 1.1 }} />
        </motion.g>

        {/* Isometric block (execution / architecture) — top right */}
        <motion.g
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 1.2, ease: [0.7, 0, 0.3, 1] }}
          stroke="#0A192F"
          strokeOpacity="0.4"
          strokeWidth="1"
          fill="none"
        >
          {/* isometric cube (single volume) */}
          <path d="M 620 140 L 700 180 L 700 260 L 620 300 L 540 260 L 540 180 Z" />
          <path d="M 620 140 L 620 220 L 700 260" />
          <path d="M 620 220 L 540 180" />
          {/* top face gold fill (very muted) */}
          <path d="M 620 140 L 700 180 L 620 220 L 540 180 Z" fill="#C5A059" fillOpacity="0.1" stroke="#C5A059" strokeOpacity="0.4" />
          {/* dimension marker */}
          <line x1="720" y1="180" x2="740" y2="180" strokeOpacity="0.3" />
          <line x1="720" y1="260" x2="740" y2="260" strokeOpacity="0.3" />
          <line x1="735" y1="180" x2="735" y2="260" strokeOpacity="0.3" />
        </motion.g>

        {/* Blueprint annotations */}
        <motion.g
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.6 }}
          fontFamily="'JetBrains Mono', ui-monospace, monospace"
          fill="#0A192F"
          fillOpacity="0.4"
          fontSize="10"
          letterSpacing="0.18em"
        >
          <text x="745" y="220" textAnchor="start">01/A</text>
          <text x="200" y="150" letterSpacing="0.24em" fontSize="9">R = 260</text>
          <line x1="200" y1="158" x2="290" y2="158" stroke="#0A192F" strokeOpacity="0.18" strokeWidth="0.6" strokeDasharray="1 3" />
        </motion.g>

        {/* Small floating architectural plan (bottom-left of composition, isometric) */}
        <motion.g
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 1.4 }}
          stroke="#0A192F"
          strokeOpacity="0.36"
          strokeWidth="0.9"
          fill="none"
        >
          <path d="M 230 560 L 320 600 L 320 660 L 230 620 Z" />
          <path d="M 320 600 L 380 570 L 380 630 L 320 660" />
          <path d="M 275 580 L 275 640" strokeOpacity="0.22" />
          <path d="M 230 590 L 275 610" strokeOpacity="0.22" />
        </motion.g>

        {/* Dual ribbons — echoing the logo wave, sweeping across the composition */}
        <motion.path
          d="M 60 470 C 200 420, 320 540, 470 470 S 720 400, 780 460"
          fill="none"
          stroke="url(#ribbon-navy)"
          strokeWidth="2.4"
          strokeLinecap="round"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 2.0, delay: 0.5, ease: [0.7, 0, 0.3, 1] }}
        />
        <motion.path
          d="M 60 490 C 220 450, 340 560, 480 495 S 730 430, 790 480"
          fill="none"
          stroke="url(#ribbon-gold)"
          strokeWidth="2.2"
          strokeLinecap="round"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 2.2, delay: 0.75, ease: [0.7, 0, 0.3, 1] }}
        />

        {/* Ribbon breathing motion */}
        <motion.g
          animate={{ y: [0, -6, 0] }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
        >
          <path
            d="M 60 470 C 200 420, 320 540, 470 470 S 720 400, 780 460"
            fill="none"
            stroke="#0A192F"
            strokeOpacity="0.15"
            strokeWidth="10"
            strokeLinecap="round"
            style={{ filter: "blur(6px)" }}
          />
        </motion.g>

        {/* Precision network — dots + connectors (procurement network) */}
        <motion.g
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.4, delay: 1.4 }}
          stroke="#0A192F"
          strokeOpacity="0.18"
          strokeWidth="0.8"
        >
          <line x1="470" y1="380" x2="620" y2="220" strokeDasharray="2 4" />
          <line x1="470" y1="380" x2="280" y2="600" strokeDasharray="2 4" />
          <line x1="470" y1="380" x2="700" y2="540" strokeDasharray="2 4" />
        </motion.g>
        <g fill="#C5A059">
          <NetworkNode x={470} y={380} r={4} />
          <NetworkNode x={620} y={220} r={3} />
          <NetworkNode x={280} y={600} r={3} />
          <NetworkNode x={700} y={540} r={3} />
        </g>

        {/* Floating particles */}
        {[
          { x: 160, y: 210, d: 8 },
          { x: 720, y: 340, d: 10 },
          { x: 260, y: 320, d: 11 },
        ].map((p, i) => (
          <motion.circle
            key={i}
            cx={p.x}
            cy={p.y}
            r="1.6"
            fill={i % 2 === 0 ? "#C5A059" : "#0A192F"}
            opacity="0.55"
            animate={{ y: [0, -14, 0], opacity: [0.4, 0.9, 0.4] }}
            transition={{ duration: p.d, repeat: Infinity, ease: "easeInOut", delay: i * 0.4 }}
          />
        ))}

        {/* Corner dimension markers (blueprint style) */}
        <g stroke="#0A192F" strokeOpacity="0.24" strokeWidth="0.8" fill="none">
          <path d="M 30 30 L 60 30 M 30 30 L 30 60" />
          <path d="M 770 30 L 740 30 M 770 30 L 770 60" />
          <path d="M 30 770 L 60 770 M 30 770 L 30 740" />
          <path d="M 770 770 L 740 770 M 770 770 L 770 740" />
        </g>

        {/* Section label */}
        <g fontFamily="'JetBrains Mono', ui-monospace, monospace" fontSize="8" fill="#0A192F" fillOpacity="0.4" letterSpacing="0.24em">
          <text x="40" y="50">NV — 001</text>
          <text x="760" y="50" textAnchor="end">SCALE 1:1</text>
          <text x="40" y="765">IDEAS → EXECUTION</text>
          <text x="760" y="765" textAnchor="end">FIG. A</text>
        </g>
      </motion.svg>

      {/* Slow light trail (CSS) */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="nv-trail" />
      </div>
    </div>
  );
}

function NetworkNode({ x, y, r }) {
  return (
    <>
      <circle cx={x} cy={y} r={r} fill="#C5A059" />
      <circle cx={x} cy={y} r={r + 4} fill="none" stroke="#C5A059" strokeOpacity="0.35" strokeWidth="0.8" />
    </>
  );
}
